/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { noop } from 'lodash'

import { addToast } from '@modules/cui/Toasts'

export type NotificationOptions = {
  title?: string
  text?: string
  color?: string
  iconType?: string
}

export type Notifications = {
  addSuccess: (options?: NotificationOptions) => void
  addWarning: (options?: NotificationOptions) => void
  addDanger: (options?: NotificationOptions) => void
  addError: (options?: NotificationOptions) => void
  add: (options?: NotificationOptions) => void
  remove: () => void
}

/**
 * Creates a notifications adapter for OneConsole
 * Wraps the CUI toast system with OneConsole-specific defaults
 */
export const createNotifications = (): Notifications => ({
  addSuccess: (options: NotificationOptions = {}) =>
    addToast({
      color: 'success',
      iconType: 'check',
      title: options.title || 'Success',
      text: options.text,
      family: 'one-console-success',
    }),
  addWarning: (options: NotificationOptions = {}) =>
    addToast({
      color: 'warning',
      iconType: 'alert',
      title: options.title || 'Warning',
      text: options.text,
      family: 'one-console-warning',
    }),
  addDanger: (options: NotificationOptions = {}) =>
    addToast({
      color: 'danger',
      iconType: 'alert',
      title: options.title || 'Error',
      text: options.text,
      family: 'one-console-danger',
    }),
  addError: (options: NotificationOptions = {}) =>
    addToast({
      color: 'danger',
      iconType: 'alert',
      title: options.title || 'Error',
      text: options.text,
      family: 'one-console-error',
    }),
  add: (options: NotificationOptions = {}) =>
    addToast({
      color: options.color || 'primary',
      iconType: options.iconType || 'iInCircle',
      title: options.title || 'Notification',
      text: options.text,
      family: 'one-console-general',
    }),
  // Note: CUI toasts auto-remove, but we could implement removal if needed
  // For now, this is a no-op as CUI handles toast lifecycle
  remove: noop,
})
